import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { useNavigate } from "react-router-dom"
import { Avatar, Box, Divider, Grid, IconButton, Menu, Stack, Tooltip, Typography } from "@mui/material"
import {
    faArrowRightArrowLeft,
    faArrowRightFromBracket,
    faCircleQuestion,
    faGear,
    faLocationDot,
    faMegaphone,
    faMonitorWaveform,
    faRoute,
    faUser,
} from "@fortawesome/pro-light-svg-icons"
import { AvatarMenuItem } from "./AvatarMenuItem"
import {
    getGoPayNewsRoute,
    getProfileRoute,
    getSettingsRoute,
    LOGIN_ROUTE,
    MERCHANT_REPORTS_ROUTE,
    SERVICE_CENTER_REPORTS_PROFILE_ROUTE,
    USER_REPORTS_GOPAY_NEWS_ROUTE,
    USER_REPORTS_PROFILE_ROUTE,
} from "../../../constants/routes"
import { systemStatusUrl } from "../../../constants/apiUrl"
import { HelpDialog } from "../../components/dialogs/helpDialog/HelpDialog"
import { selectUserInitials, selectUsername, selectUserType } from "../../../modules/login/loginSelectors"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { useAppDispatch, useAppSelector } from "../../../modules/store/storeHooks"
import { logout } from "../../../modules/login/loginSlice"
import { selectEffectiveUserType } from "../../../modules/context/contextSelectors"
import { UserType } from "../../../modules/login/loginTypes"
import {
    SwitchMerchantDialog
} from "../../../modules/merchant/admin/account/merchantAccount/components/SwitchMerchantDialog"
import { ChangeUserAccountDialog } from "../../../modules/profile/channgeUserAccount/components/ChangeUserAccountDialog"
import { MatchEnvironment } from "../../../modules/environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../modules/environment/envTypes"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { MatchActualUserType } from "../../../modules/accessControl/MatchActualUserType"
import { HideForCurrentUserType } from "../../../modules/accessControl/HideForCurrentUserType"
import { selectLoginUser } from "../../../modules/users/userSelectors"
import { useExternalLinks } from "../../hooks/useAppLinks"
import { LocalCache, StorageKey } from "../../cache/LocalCache"
import { useMatchEnvironment } from "../../../modules/environment/envHooks"

type AvatarMenuProps = Readonly<{
    urlPrefix: string | undefined
}>

const useStyles = makeStyles()(() => ({
    userAvatar: {
        backgroundColor: tailwindPalette.slate[600],
        color: "white",
    },
    logoutIcon: {
        color: "#ef5350",
    },
    avatarButton: {
        padding: 0,
        marginLeft: "0.5rem",
    },
    menu: {
        marginTop: "2.8125rem",
    },
    menuContent: {
        padding: ".7rem",
    },
    menuAvatarButton: {
        marginBottom: "0.2rem",
    },
    divider: {
        marginLeft: "1rem",
        marginRight: "1rem",
        marginBottom: "0.5rem",
    },
}))

export function AvatarMenu({ urlPrefix }: AvatarMenuProps) {
    const navigate = useNavigate()
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const userInitials = useAppSelector(selectUserInitials)
    const currentUserType = useAppSelector(selectEffectiveUserType)
    const username = useAppSelector(selectUsername)
    const userDetails = useAppSelector(selectLoginUser)
    const dispatch = useAppDispatch()
    const openExternalLink = useExternalLinks()
    const localCache = LocalCache()
    const isDividerBeforeNewsVisible = useMatchEnvironment([
        EnvironmentType.Local,
        EnvironmentType.Test,
        EnvironmentType.Demo,
        EnvironmentType.QA,
    ]) || currentUserType === UserType.USER || currentUserType === UserType.SERVICE_CENTER

    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
    const [showSwiftMerchantModal, setShowSwiftMerchantModal] = useState(false)
    const [showChangeUserAccountModal, setShowChangeUserAccountModal] = useState(false)
    const [showHelpDialog, setShowHelpDialog] = useState(false)
    const actualUserType = useAppSelector(selectUserType)

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget)
    }

    const handleCloseUserMenu = () => {
        setAnchorElUser(null)
    }

    const navigateToProfilePage = () => {
        handleCloseUserMenu()
        if (currentUserType === UserType.USER) {
            navigate(`${USER_REPORTS_PROFILE_ROUTE}?tab=0`)
            return
        }

        if (currentUserType === UserType.SERVICE_CENTER) {
            navigate(`${SERVICE_CENTER_REPORTS_PROFILE_ROUTE}`)
            return
        }
        navigate(getProfileRoute(urlPrefix))
    }

    function navigateToSettingsPage() {
        handleCloseUserMenu()
        if (currentUserType === UserType.USER) {
            navigate(`${USER_REPORTS_PROFILE_ROUTE}?tab=1`)
            return
        }
        navigate(getSettingsRoute(urlPrefix))
    }

    function handleLogout() {
        dispatch(logout())

        // Not strictly needed since this route is access controlled
        navigate(LOGIN_ROUTE)
    }

    const handleOpenSwiftMerchantModal = () => {
        setShowSwiftMerchantModal(true)
        handleCloseUserMenu()
    }

    const handleOpenChangeUserAccountModal = () => {
        setShowChangeUserAccountModal(true)
        handleCloseUserMenu()
    }

    const handleOpenHelpDialog = () => {
        setShowHelpDialog(true)
        handleCloseUserMenu()
    }

    const handleStartMerchantProductTour = () => {
        localCache.set(StorageKey.MERCHANT_PRODUCT_TOUR_FORCE_RUN, "1")
        window.dispatchEvent(new CustomEvent("merchant-product-tour:start"))
        navigate(MERCHANT_REPORTS_ROUTE)
        handleCloseUserMenu()
    }

    const handleNavigateToNews = () => {
        handleCloseUserMenu()
        if (actualUserType === UserType.USER) {
            navigate(USER_REPORTS_GOPAY_NEWS_ROUTE)
            return
        }
        navigate(getGoPayNewsRoute(urlPrefix))
    }

    const handleOpenSystemStatus = () => {
        handleCloseUserMenu()
        openExternalLink(systemStatusUrl)
    }

    return (
        <>
            <Tooltip title={l10n.getString("avatar-menu-tooltip")}>
                <IconButton onClick={handleOpenUserMenu} className={classes.avatarButton}>
                    <Avatar className={classes.userAvatar}>
                        {userInitials?.toUpperCase() ?? userDetails?.displayInitials ?? "ME"}
                    </Avatar>
                </IconButton>
            </Tooltip>
            <Menu
                className={classes.menu}
                id="mobile-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                <Box className={classes.menuContent}>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="center" minWidth="15rem">
                        <IconButton className={classes.menuAvatarButton}>
                            <Avatar className={classes.userAvatar}>{userInitials?.toUpperCase() ?? "ME"}</Avatar>
                        </IconButton>
                        <Stack direction="column">
                            <HideForCurrentUserType userTypes={[UserType.USER]}>
                                <Typography variant="body1">{username}</Typography>
                            </HideForCurrentUserType>
                            <MatchActualUserType userTypes={[UserType.USER]}>
                                <Typography variant="body1">{userDetails?.displayName}</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {username}
                                </Typography>
                            </MatchActualUserType>
                        </Stack>
                    </Grid>

                    <Divider className={classes.divider} />

                    {/* User account */}
                    <AvatarMenuItem
                        icon={faUser}
                        tooltipLocalizationId="avatar-menu-tooltip-profile"
                        menuItemLocalizationId="avatar-menu-menu-item-profile"
                        tooltipFallbackText="View your user account"
                        menuItemFallbackText="User account"
                        onClick={navigateToProfilePage}
                    />

                    {/* Settings */}
                    <HideForCurrentUserType userTypes={[UserType.USER, UserType.SERVICE_CENTER]}>
                        <AvatarMenuItem
                            icon={faGear}
                            tooltipLocalizationId="avatar-menu-tooltip-settings"
                            menuItemLocalizationId="avatar-menu-menu-item-settings"
                            tooltipFallbackText="Change settings for your user account"
                            menuItemFallbackText="Settings"
                            onClick={navigateToSettingsPage}
                        />
                        <Divider className={classes.divider} />
                    </HideForCurrentUserType>

                    <MatchEnvironment
                        environments={[
                            EnvironmentType.Local,
                            EnvironmentType.Test,
                            EnvironmentType.Demo,
                            EnvironmentType.QA,
                        ]}
                    >
                        {/* Switch user account */}
                        <AvatarMenuItem
                            icon={faArrowRightArrowLeft}
                            tooltipLocalizationId="avatar-menu-tooltip-change-user-account"
                            menuItemLocalizationId="avatar-menu-menu-item-change-user-account"
                            tooltipFallbackText="If you have multiple user accounts, you can switch between them here"
                            menuItemFallbackText="Switch user account"
                            onClick={handleOpenChangeUserAccountModal}
                        />

                        {/* Switch merchant */}
                        {currentUserType === UserType.MERCHANT && (
                            <AvatarMenuItem
                                icon={faLocationDot}
                                tooltipLocalizationId="avatar-menu-tooltip-switch-merchant"
                                menuItemLocalizationId="avatar-menu-menu-item-swift-merchant"
                                tooltipFallbackText="If you have access to more than one merchant, you can switch between them here"
                                menuItemFallbackText="Switch merchant"
                                onClick={handleOpenSwiftMerchantModal}
                            />
                        )}
                    </MatchEnvironment>

                    {isDividerBeforeNewsVisible && <Divider className={classes.divider} />}

                    {/* Facilitynet news */}
                    <AvatarMenuItem
                        icon={faMegaphone}
                        tooltipLocalizationId="avatar-menu-tooltip-news"
                        menuItemLocalizationId="avatar-menu-menu-item-news"
                        tooltipFallbackText="Read news from Facilitynet"
                        menuItemFallbackText="Facilitynet news"
                        onClick={handleNavigateToNews}
                    />

                    <Divider className={classes.divider} />

                    {/* Help */}
                    <MatchEnvironment
                        environments={[
                            EnvironmentType.Local,
                            EnvironmentType.Test,
                            EnvironmentType.Demo,
                            EnvironmentType.QA,
                        ]}
                    >
                        <AvatarMenuItem
                            icon={faCircleQuestion}
                            tooltipLocalizationId="avatar-menu-tooltip-help"
                            menuItemLocalizationId="avatar-menu-menu-item-help"
                            tooltipFallbackText="Get help for using the system"
                            menuItemFallbackText="Help"
                            onClick={handleOpenHelpDialog}
                        />
                        {currentUserType === UserType.MERCHANT && (
                            <AvatarMenuItem
                                icon={faRoute}
                                tooltipLocalizationId="avatar-menu-tooltip-merchant-tour"
                                menuItemLocalizationId="avatar-menu-menu-item-merchant-tour"
                                tooltipFallbackText="Start product tour"
                                menuItemFallbackText="Tour guide"
                                onClick={handleStartMerchantProductTour}
                            />
                        )}
                    </MatchEnvironment>

                    {/* System status */}
                    <AvatarMenuItem
                        icon={faMonitorWaveform}
                        tooltipLocalizationId="avatar-menu-tooltip-system-status"
                        menuItemLocalizationId="avatar-menu-menu-item-system-status"
                        tooltipFallbackText="View and subscribe to important messages about the system"
                        menuItemFallbackText="System status"
                        onClick={handleOpenSystemStatus}
                    />

                    <Divider className={classes.divider} />

                    {/* Log out */}
                    <AvatarMenuItem
                        icon={faArrowRightFromBracket}
                        tooltipLocalizationId="avatar-menu-tooltip-logout"
                        menuItemLocalizationId="avatar-menu-menu-item-logout"
                        tooltipFallbackText="Log out of the system"
                        menuItemFallbackText="Log out"
                        onClick={handleLogout}
                        iconClassName={classes.logoutIcon}
                    />
                </Box>
            </Menu>
            {showSwiftMerchantModal && (
                <SwitchMerchantDialog open={showSwiftMerchantModal} onClose={() => setShowSwiftMerchantModal(false)} />
            )}
            {showChangeUserAccountModal && (
                <ChangeUserAccountDialog
                    open={showChangeUserAccountModal}
                    onClose={() => setShowChangeUserAccountModal(false)}
                />
            )}
            {showHelpDialog && <HelpDialog dialogState={showHelpDialog} setDialogState={setShowHelpDialog} />}
        </>
    )
}
